import { logger } from '~/lib/logger';

/**
 * Web image preparation. Same contract as image.ts, canvas instead of the native
 * compressor — react-native-compressor has no working web implementation, and a
 * canvas downscale is a few lines rather than a WASM dependency.
 *
 * Web also needs this: the signed-URL upload path sends the bytes directly, so
 * an un-downscaled 12 MP phone photo is a slow upload on a field connection even
 * though it does not hit the 28 MB body cap.
 */

export const MAX_EDGE = 1600;
const QUALITY = 0.8;

export interface PreparedImage {
  base64: string;
  uri: string;
  bytes: number;
}

export function decodedBytes(base64: string): number {
  return Math.floor((base64.length * 3) / 4);
}

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('could not decode image'));
    img.src = uri;
  });
}

export async function prepareImage(uri: string): Promise<PreparedImage> {
  const img = await loadImage(uri);
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  // Never upscale — a small photo stays as it is rather than being interpolated
  // into a bigger file with no extra detail.
  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const bytes = decodedBytes(base64);
  logger.log('[image] prepared', {
    w: canvas.width,
    h: canvas.height,
    kb: Math.round(bytes / 1024),
  });
  return { base64, uri: dataUrl, bytes };
}
